import { useEffect, useState } from "react";
import apiRequest from "../../../../../../utils/ApiRequest/apiRequest.js";
import PaymentTypeList from "../../../../Purchases_new/components/PaymentMethodSection/components/PaymentTypeList";
import useCreditCard from "../../../../Purchases_new/hooks/useCreditCard.ts";
import { useContent } from "../../../../../../contexts/contentContext/contentContext.js";
import { PaymentTypeE } from "../../../../Purchases_new/models/enums.ts";
import { useUser } from "../../../../../../contexts/userContext/userContext.js";
import type {
  BillingDetailsT,
  UserDataT,
} from "../../../../../../models/models.ts";

interface CreditCardModalI {
  url: string;
  getLastCreditCardDetails: () => void;
}

const REDIRECT_TO_SUCCESS_PAGE_COUNT = 2;

/**
 * Renders a modal for managing credit card information.
 *
 * @component
 * @param {CreditCardModalI} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
const CreditCardModal = (props: CreditCardModalI) => {
  const { url, getLastCreditCardDetails } = props;
  const { fetchCreditCards, creditCards, setSelectedCreditCardId } =
    useCreditCard();
  const [showPaymentTypeList, setShowPaymentTypeList] = useState(true);
  const { getTextByKey } = useContent();
  const { getUserBillingDetails, getUserData, setUserBillingDetailsDataProp } =
    useUser();
  const billingDetails = getUserBillingDetails() as BillingDetailsT;
  const userData = getUserData() as UserDataT;
  const [loading, setLoading] = useState(false);

  // Counts redirects within iframe
  const [iframeOnloadEventCounter, setIframeOnloadEventCounter] = useState(0);

  useEffect(() => {
    fetchCreditCards();
  }, []);

  useEffect(() => {
    if (creditCards?.length && billingDetails?.token_id) {
      setSelectedCreditCardId(billingDetails.token_id);
    }
  }, [creditCards, billingDetails?.token_id]);

  useEffect(() => {
    if (iframeOnloadEventCounter === REDIRECT_TO_SUCCESS_PAGE_COUNT) {
      window.location.reload();
    }
  }, [iframeOnloadEventCounter]);

  /**
   * Handles the change of payment type and updates the selected credit card.
   * If the payment type is NEW_CREDIT_CARD, it hides the payment type list
   * and shows iframe for new credit card.
   * Otherwise, it sets the selected credit card as the primary credit card for the user.
   *
   * @param paymentType - The selected payment type.
   * @param creditCardId - The ID of the selected credit card.
   */
  const handleChange = async (
    paymentType: PaymentTypeE | null,
    creditCardId: number
  ) => {
    if (paymentType === PaymentTypeE.NEW_CREDIT_CARD) {
      setShowPaymentTypeList(false);
    } else {
      const payload = {
        userId: userData?.UserId,
        creditCardTokenId: creditCardId,
      };

      setLoading(true);
      await apiRequest("Users", "SetPrimaryCreditCard", payload, "PUT");
      setUserBillingDetailsDataProp("token_id", creditCardId);
      setSelectedCreditCardId(creditCardId);

      await getLastCreditCardDetails();
      setLoading(false);
      window.location.reload();
    }
  };

  return (
    <div className="credit-modal">
      <div className="modal-body">
        <div className="modal-text">
          <h1>{getTextByKey("payment-method-cc-title")}</h1>
        </div>
        {showPaymentTypeList ? (
          <div
            className={`payment-type-list-wrapper ${
              loading ? "payment-method-loading" : ""
            }`}
          >
            <PaymentTypeList
              setShowPaymentTypeList={setShowPaymentTypeList}
              showPayPal={false}
              onChange={handleChange}
            />
          </div>
        ) : url ? (
          <div className="paymentIframe_currentDIR">
            <iframe
              title={url}
              src={url}
              scrolling="no"
              width="400px"
              height="450px"
              onLoad={(e) => {
                setIframeOnloadEventCounter((prev) => prev + 1);
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CreditCardModal;
